import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useResource } from '../../MenuItems/hooks/useResource';
import { useRestaurantId, useRestaurantInfo } from '../../MenuItems/hooks/useRestaurant';
import type { ItemRecord } from '../../MenuItems/types';
import { newCategory, saveMenu, toEditorState } from '../methods';
import type { EditorState, MenuRecord } from '../types';

/** Loads the menu, its restaurant and the item catalogue, and owns the editor state. */
export const useMenuForm = (menuId?: string) => {
    const activeRestaurantId = useRestaurantId();
    const menu = useResource<MenuRecord>(menuId ? `/menus/${menuId}` : null);
    const restaurantId = menuId ? (menu.data?.restaurantId ?? null) : activeRestaurantId;
    const restaurant = useRestaurantInfo(restaurantId);
    const catalogue = useResource<ItemRecord[]>(restaurantId ? `/restaurants/${restaurantId}/menu-items` : null);

    const [state, setState] = useState<EditorState | null>(null);
    const [baseline, setBaseline] = useState('');
    const progress = useRef<{ menuId: string | null; attached: Set<string> }>({ menuId: menuId ?? null, attached: new Set() });

    const resources = [restaurant, catalogue, ...(menuId ? [menu] : [])];
    const failed = resources.some((r) => r.status === 'error');
    const ready = !failed && resources.every((r) => r.status === 'ready') && (!menuId || menu.data?.id === menuId);
    const languages = useMemo(
        () => (restaurant.data?.supportedLanguages.length ? restaurant.data.supportedLanguages : ['en']),
        [restaurant.data]
    );
    const items = useMemo(() => (catalogue.data ?? []).filter((i) => !i.isArchived), [catalogue.data]);

    useEffect(() => {
        if (!ready || state) return;
        const initial = toEditorState(menu.data, languages, items);
        progress.current.attached = new Set(initial.categories.flatMap((c) => (c.id ? [c.id] : [])));
        setState(initial);
        setBaseline(JSON.stringify(initial));
    }, [ready, state, menu.data, languages, items]);

    const update = (change: (s: EditorState) => EditorState) => setState((s) => (s ? change(s) : s));

    const actions = {
        setActive: (isActive: boolean) => update((s) => ({ ...s, isActive })),
        setName: (lang: string, value: string) => update((s) => ({ ...s, names: { ...s.names, [lang]: value } })),
        setDescription: (lang: string, value: string) => update((s) => ({ ...s, descriptions: { ...s.descriptions, [lang]: value } })),
        addCategory: (): string => {
            const category = newCategory(languages);
            update((s) => ({ ...s, categories: [...s.categories, category] }));
            return category.key;
        },
        renameCategory: (key: string, lang: string, value: string) =>
            update((s) => ({
                ...s,
                categories: s.categories.map((c) => (c.key === key ? { ...c, names: { ...c.names, [lang]: value } } : c)),
            })),
        removeCategory: (key: string) => update((s) => ({ ...s, categories: s.categories.filter((c) => c.key !== key) })),
        moveCategory: (from: number, to: number) =>
            update((s) => {
                if (to < 0 || to >= s.categories.length) return s;
                const categories = [...s.categories];
                categories.splice(to, 0, ...categories.splice(from, 1));
                return { ...s, categories };
            }),
        addItems: (key: string, ids: string[]) =>
            update((s) => ({
                ...s,
                categories: s.categories.map((c) =>
                    c.key === key ? { ...c, itemIds: [...c.itemIds, ...ids.filter((id) => !c.itemIds.includes(id))] } : c
                ),
            })),
        removeItem: (key: string, itemId: string) =>
            update((s) => ({
                ...s,
                categories: s.categories.map((c) => (c.key === key ? { ...c, itemIds: c.itemIds.filter((id) => id !== itemId) } : c)),
            })),
        /** Within a category or between two. */
        moveItem: (fromKey: string, fromIndex: number, toKey: string, toIndex: number) =>
            update((s) => {
                const source = s.categories.find((c) => c.key === fromKey);
                const itemId = source?.itemIds[fromIndex];
                if (!source || itemId === undefined) return s;
                const target = s.categories.find((c) => c.key === toKey);
                if (!target || toIndex < 0 || toIndex > (fromKey === toKey ? target.itemIds.length - 1 : target.itemIds.length)) return s;
                if (fromKey !== toKey && target.itemIds.includes(itemId)) return s;
                return {
                    ...s,
                    categories: s.categories.map((c) => {
                        const ids = c.key === fromKey ? c.itemIds.filter((_, i) => i !== fromIndex) : c.itemIds;
                        if (c.key !== toKey) return c.key === fromKey ? { ...c, itemIds: ids } : c;
                        const next = [...ids];
                        next.splice(toIndex, 0, itemId);
                        return { ...c, itemIds: next };
                    }),
                };
            }),
    };

    /** Saves everything. Throws on failure; a retry continues where it stopped. */
    const save = async (): Promise<string> => {
        if (!state || !restaurantId) throw new Error('Menu is not loaded');
        const id = await saveMenu(restaurantId, state, progress.current, {
            menuCreated: (created) => {
                progress.current.menuId = created;
            },
            categoryAttached: (key, categoryId) => {
                progress.current.attached.add(categoryId);
                setState((s) => (s ? { ...s, categories: s.categories.map((c) => (c.key === key ? { ...c, id: categoryId } : c)) } : s));
            },
            categoryDetached: (categoryId) => {
                progress.current.attached.delete(categoryId);
            },
        });
        return id;
    };

    const setItems: Dispatch<SetStateAction<ItemRecord[] | null>> = catalogue.setData;

    return {
        state,
        dirty: state !== null && JSON.stringify(state) !== baseline,
        missing: menuId !== undefined && menu.missing,
        status: failed ? ('error' as const) : state ? ('ready' as const) : ('loading' as const),
        reload: () => {
            menu.reload();
            restaurant.reload();
            catalogue.reload();
        },
        restaurant: restaurant.data,
        languages,
        items,
        setItems,
        actions,
        save,
    };
};
